import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { useDarkMode } from '../context/DarkModeContext'

const ChromeIcon = ({ className }) => (
	<svg className={className} viewBox="0 0 24 24" fill="currentColor">
		<path d="M12 0C8.21 0 4.831 1.757 2.632 4.501l3.953 6.848A5.454 5.454 0 0 1 12 6.545h10.691A12 12 0 0 0 12 0zM1.931 5.47A11.943 11.943 0 0 0 0 12c0 6.012 4.42 10.991 10.189 11.864l3.953-6.847a5.45 5.45 0 0 1-6.865-2.29zm13.342 2.166a5.446 5.446 0 0 1 1.45 7.09l.002.001h-.002l-5.344 9.257c.206.01.413.016.621.016 6.627 0 12-5.373 12-12 0-1.54-.29-3.011-.818-4.364zM12 16.364a4.364 4.364 0 1 1 0-8.728 4.364 4.364 0 0 1 0 8.728z"/>
	</svg>
)

const loadFetcherLogo = '/logo512.png'
const loadLineLogo = '/loadline-logo512 .png'

function classNames(...classes) {
	return classes.filter(Boolean).join(' ')
}

const NineDotIcon = ({ className }) => (
	<svg className={className} viewBox="0 0 24 24" fill="currentColor">
		<circle cx="5" cy="5" r="2" />
		<circle cx="12" cy="5" r="2" />
		<circle cx="19" cy="5" r="2" />
		<circle cx="5" cy="12" r="2" />
		<circle cx="12" cy="12" r="2" />
		<circle cx="19" cy="12" r="2" />
		<circle cx="5" cy="19" r="2" />
		<circle cx="12" cy="19" r="2" />
		<circle cx="19" cy="19" r="2" />
	</svg>
)

export default function AppLauncher() {
	const { isDarkMode } = useDarkMode()

	const apps = [
		{
			name: 'LoadFetcher',
			description: 'For Relay Amazon',
			href: 'https://chrome.google.com/webstore/detail/relay-auto-refresher-post/ihcgicdogclbieclnldfjmlglkolankb',
			logo: loadFetcherLogo,
		},
		{
			name: 'LoadLine',
			description: 'For DAT.com',
			href: 'https://chromewebstore.google.com/detail/loadline-all-in-one-dispa/cpcnkckpbpefgkcilhclfdefccflgaif',
			logo: loadLineLogo,
		},
	]

	const handleOpenLink = (href) => {
		window.open(href, '_blank')
	}

	return (
		<Menu as="div" className="relative inline-block text-left">
			{() => (
				<>
					<div>
						<Menu.Button className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 transition-colors">
							<span className="sr-only">Open apps</span>
							<NineDotIcon className="h-6 w-6" aria-hidden="true" />
						</Menu.Button>
					</div>

					<Transition
						as={Fragment}
						enter="transition ease-out duration-100"
						enterFrom="transform opacity-0 scale-95"
						enterTo="transform opacity-100 scale-100"
						leave="transition ease-in duration-75"
						leaveFrom="transform opacity-100 scale-100"
						leaveTo="transform opacity-0 scale-95"
					>
						<Menu.Items className={classNames(
							'absolute right-0 z-50 mt-2 w-60 origin-top-right rounded-lg shadow-xl ring-1 focus:outline-none overflow-hidden',
							isDarkMode
								? 'bg-[#0D1117] ring-gray-700'
								: 'bg-white ring-gray-300'
						)}>
							<div className={classNames(
								'px-4 py-3 border-b',
								isDarkMode ? 'border-gray-700' : 'border-gray-200'
							)}>
								<div className="flex items-center">
									<ChromeIcon className={classNames(
										'h-5 w-5 mr-2',
										isDarkMode ? 'text-sky-400' : 'text-sky-600'
									)} />
									<h3 className={classNames(
										'text-sm font-semibold',
										isDarkMode ? 'text-white' : 'text-gray-900'
									)}>
										Install Chrome Extensions
									</h3>
								</div>
							</div>
							<div className="py-2">
								{apps.map((app, index) => (
									<Menu.Item key={app.name}>
										{({ active }) => (
											<button
												onClick={() => handleOpenLink(app.href)}
												className={classNames(
													'flex items-center w-full px-4 py-3 transition-all text-left group',
													isDarkMode
														? active
															? 'bg-gray-800'
															: 'hover:bg-gray-800/50'
														: active
														? 'bg-sky-50'
														: 'hover:bg-gray-50'
												)}
											>
												<div className="w-8 h-8 rounded-full mr-3 flex-shrink-0 overflow-hidden bg-white">
													<img
														src={app.logo}
														alt={app.name}
														className="w-full h-full object-cover"
													/>
												</div>
												<div className="flex-1 min-w-0">
													<div className={classNames(
														'text-sm font-semibold mb-0.5',
														isDarkMode ? 'text-white' : 'text-gray-900'
													)}>
														{app.name}
													</div>
													<div className={classNames(
														'text-xs',
														isDarkMode ? 'text-gray-400' : 'text-gray-600'
													)}>
														{app.description}
													</div>
												</div>
											</button>
										)}
									</Menu.Item>
								))}
							</div>
						</Menu.Items>
					</Transition>
				</>
			)}
		</Menu>
	)
}
